<template>
    <v-dialog :model-value="modelValue" @update:model-value="onDialogToggle" max-width="70vw">
        <v-card class="import-offer-dialog-card">
            <v-toolbar class="d-flex align-items-center justify-content-between import-offer-dialog-toolbar">
                <v-toolbar-title>Importă ofertă existentă</v-toolbar-title>
                <v-btn icon variant="text" @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-card-text class="import-offer-dialog-content">
                <v-row>
                    <v-col cols="12" md="3">
                        <v-select
                            :items="offerTypeItems"
                            item-title="name"
                            item-value="type"
                            v-model="selectedImportTypeCode"
                            label="Tip ofertă"
                            density="compact"
                            clearable
                            :variant="fieldVariant"
                            @update:model-value="onImportFilterChanged"
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-model="importSearch"
                            label="Nume client"
                            density="compact"
                            clearable
                            :variant="fieldVariant"
                        />
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-text-field
                            v-model="importOfferId"
                            label="Nr. ofertă"
                            density="compact"
                            clearable
                            :variant="fieldVariant"
                        />
                    </v-col>
                    <v-col cols="12" md="2" class="d-flex justify-end">
                        <v-btn color="primary" :loading="importLoading" @click="onImportSearch">
                            Caută
                        </v-btn>
                    </v-col>
                </v-row>
               
                <v-data-table-server
                    v-if="showImportResultsTable"
                    :headers="importHeaders"
                    :items="importOffers"
                    item-value="id"
                    density="compact"
                    :loading="importLoading"
                    class="mt-2 elevation-1"
                    dense
                    :items-length="importTotalCount"
                    :page="importPage"
                    :items-per-page="importItemsPerPage"
                    :sort-by="[{ key: 'modified', order: 'desc' }]"
                    :row-props="getImportRowProps"
                    @update:page="onImportPageChange"
                    @update:items-per-page="onImportItemsPerPageChange"
                    @click:row="onImportRowClick"
                >
                    <template v-slot:[`item.id`]="{ item }">#{{ item.id }}</template>
                    <template v-slot:[`item.client_name`]="{ item }">
                        {{ item.client_name || item.client?.full_name || "-" }}
                    </template>
                    <template v-slot:[`item.location_description`]="{ item }">
                        {{
                            item.location_description ||
                            item.location?.description ||
                            item.location_data?.description ||
                            "-"
                        }}
                    </template>
                    <template v-slot:[`item.type_name`]="{ item }">
                        {{ getOfferImportTypeLabel(item.type) }}
                    </template>
                    <template v-slot:[`item.modified`]="{ item }">
                        {{ formatImportDate(item.modified) }}
                    </template>
                </v-data-table-server>
                <v-alert
                    v-if="!importHasSearched"
                    class="mt-3"
                    type="info"
                    variant="tonal"
                >
                    Completați filtrele dorite și apăsați „Caută” pentru a încărca ofertele.
                </v-alert>
                <v-alert
                    v-else-if="importHasSearched && !canSearchImportOffers"
                    class="mt-3"
                    type="danger"
                    variant="tonal"
                >
                    Nu ai introdus niciun criteriu de căutare. Selectează un filtru și apasă „Caută”.
                </v-alert>
                <v-alert
                    v-else-if="importHasSearched && canSearchImportOffers && !importOffers.length && !importLoading"
                    class="mt-3"
                    type="info"
                    variant="tonal"
                >
                    Nu au fost găsite oferte pentru filtrele selectate.
                </v-alert>
                <v-alert
                    v-else-if="importLoading"
                    class="mt-3"
                    type="info"
                    variant="tonal"
                >
                    Se încarcă ofertele...
                </v-alert>
            </v-card-text>

            <v-dialog v-model="showImportConfirmDialog" max-width="520">
                <v-card>
                    <v-card-title class="text-h6">Confirmare import</v-card-title>
                    <v-card-text>
                        <p class="mb-2">{{ buildImportConfirmMessage(pendingImportOffer?.type) }}</p>
                        <p class="mb-1"><strong>Oferta:</strong> #{{ pendingImportOffer?.id || "-" }}</p>
                        <p class="mb-1">
                            <strong>Client:</strong>
                            {{ pendingImportOffer?.client_name || pendingImportOffer?.client?.full_name || "-" }}
                        </p>
                        <p class="mb-0">
                            <strong>Tip:</strong> {{ getOfferImportTypeLabel(pendingImportOffer?.type) }}
                        </p>
                    </v-card-text>
                    <v-card-actions class="justify-end">
                        <v-btn variant="text" @click="cancelImportConfirm">Anulează</v-btn>
                        <v-btn color="primary" @click="confirmImportOffer">Importă</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-card>
    </v-dialog>
</template>

<script>
import apiClient from "@/utils/apiClient";
import { showAlertModal } from "@/utils/utils";
import {
    OFFER_IMPORT_TYPES,
    getOfferImportTypeLabel,
    getOfferImportRouteName,
    importOfferCreatesNewLocation,
} from "@/constants/offerImportCatalog";
import {
    buildImportOfferListRows,
    buildImportOfferListParams,
    withImportListClientNames,
} from "@/utils/importOfferSearch";

export default {
    name: "ImportOfferDialog",
    props: {
        modelValue: { type: Boolean, default: false },
        client: { type: Object, required: true },
        location: { type: Object, required: true },
    },
    emits: ["update:modelValue"],
    data() {
        return {
            offerTypeItems: OFFER_IMPORT_TYPES,
            importLoading: false,
            importSearch: "",
            importOfferId: "",
            importOffers: [],
            selectedImportTypeCode: null,
            importPage: 1,
            importItemsPerPage: 10,
            importTotalCount: 0,
            importHasSearched: false,
            showImportConfirmDialog: false,
            pendingImportOffer: null,
            selectedImportOfferId: null,
            importHeaders: [
                { title: "Nr.", key: "id" },
                { title: "Client", key: "client_name" },
                { title: "Locație", key: "location_description" },
                { title: "Tip", key: "type_name" },
                { title: "Data", key: "modified" },
            ],
        };
    },
    computed: {
        fieldVariant() {
            return this.$store.state.vuetifyFieldType;
        },
        canSearchImportOffers() {
            return (
                !!this.selectedImportTypeCode ||
                !!(this.importSearch && this.importSearch.trim().length > 0) ||
                !!(this.importOfferId && String(this.importOfferId).trim().length > 0)
            );
        },
        showImportResultsTable() {
            return this.importHasSearched && this.canSearchImportOffers;
        },
    },
    watch: {
        modelValue(open) {
            if (open) {
                this.resetForm();
            }
        },
    },
    methods: {
        getOfferImportTypeLabel,
        onDialogToggle(val) {
            if (!val) {
                this.resetForm();
            }
            this.$emit("update:modelValue", val);
        },
        close() {
            this.resetForm();
            this.$emit("update:modelValue", false);
        },
        resetForm() {
            this.importPage = 1;
            this.importItemsPerPage = 10;
            this.selectedImportTypeCode = null;
            this.importSearch = "";
            this.importOfferId = "";
            this.importOffers = [];
            this.importTotalCount = 0;
            this.importHasSearched = false;
            this.showImportConfirmDialog = false;
            this.pendingImportOffer = null;
            this.selectedImportOfferId = null;
        },
        formatImportDate(value) {
            if (!value) return "-";
            return new Date(value).toLocaleString("ro-RO", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            });
        },
        async onImportSearch() {
            this.importHasSearched = true;
            this.importPage = 1;
            await this.fetchImportOffers();
        },
        onImportFilterChanged() {
            this.importPage = 1;
            this.importHasSearched = false;
            this.importOffers = [];
            this.importTotalCount = 0;
            this.selectedImportOfferId = null;
        },
        async onImportPageChange(page) {
            if (!this.importHasSearched) return;
            this.importPage = page;
            await this.fetchImportOffers();
        },
        async onImportItemsPerPageChange(itemsPerPage) {
            if (!this.importHasSearched) return;
            this.importItemsPerPage = itemsPerPage;
            this.importPage = 1;
            await this.fetchImportOffers();
        },
        async fetchImportOffers() {
            if (!this.canSearchImportOffers) {
                this.importOffers = [];
                this.importTotalCount = 0;
                return;
            }
            this.importLoading = true;
            try {
                const response = await apiClient.get("/api/get_offer_data/", {
                    params: buildImportOfferListParams({
                        page: this.importPage,
                        itemsPerPage: this.importItemsPerPage,
                        selectedImportTypeCode: this.selectedImportTypeCode,
                        importSearch: this.importSearch,
                        importOfferId: this.importOfferId,
                    }),
                });
                const payload = response.data?.results || response.data || {};
                const visibleRows = buildImportOfferListRows(payload);
                this.importTotalCount = response.data?.count ?? visibleRows.length ?? 0;
                this.importOffers = withImportListClientNames(visibleRows);
                if (
                    this.selectedImportOfferId &&
                    !this.importOffers.some((offer) => offer.id === this.selectedImportOfferId)
                ) {
                    this.selectedImportOfferId = null;
                }
            } catch (error) {
                showAlertModal(
                    this.$store,
                    "A apărut o eroare la încărcarea ofertelor.",
                    "danger",
                    8000,
                    error
                );
            } finally {
                this.importLoading = false;
            }
        },
        buildImportConfirmMessage(offerType) {
            if (importOfferCreatesNewLocation(offerType)) {
                return "Pentru această ofertă se va crea o locație nouă. Continui importul?";
            }
            return "Ești sigur că vrei să imporți această ofertă?";
        },
        getImportRowProps({ item }) {
            const rowProps = {
                title: "Click pentru a importa oferta",
            };
            if (item?.id === this.selectedImportOfferId) {
                return {
                    ...rowProps,
                    class: "import-offer-row-selected",
                };
            }
            return rowProps;
        },
        onImportRowClick(_event, row) {
            const selected = row?.item || row;
            if (!selected?.id) return;
            this.selectedImportOfferId = selected.id;
            this.pendingImportOffer = selected;
            this.showImportConfirmDialog = true;
        },
        cancelImportConfirm() {
            this.showImportConfirmDialog = false;
            this.pendingImportOffer = null;
        },
        async confirmImportOffer() {
            const selected = this.pendingImportOffer;
            this.showImportConfirmDialog = false;
            this.pendingImportOffer = null;
            await this.importSelectedOffer(selected);
        },
        async importSelectedOffer(selected) {
            if (!selected?.id) return;
            const routeName = getOfferImportRouteName(selected.type);
            if (!routeName) return;

            try {
                const shouldCreateLocationOnSave = importOfferCreatesNewLocation(
                    selected.type
                );
                const targetLocationId = this.location.id;

                this.close();
                sessionStorage.setItem("clientId", String(this.client.id));
                this.$router.push({
                    name: routeName,
                    params: {
                        clientId: this.client.id,
                        locationId: targetLocationId,
                    },
                    query: {
                        importOfferId: selected.import_source_offer_id ?? selected.id,
                        importCreateLocation: shouldCreateLocationOnSave ? "1" : undefined,
                        importLocationDescription: this.location?.description || undefined,
                        importCompositeId:
                            typeof selected.id === "string" && /^C(?:O)?\d+$/i.test(selected.id)
                                ? selected.id
                                : undefined,
                    },
                });
            } catch (error) {
                showAlertModal(
                    this.$store,
                    "Nu am putut crea locația nouă pentru import.",
                    "danger",
                    9000,
                    error
                );
            }
        },
    },
};
</script>

<style scoped>
.import-offer-dialog-card {
    max-height: 85vh;
    overflow: hidden;
}

.import-offer-dialog-toolbar {
    position: sticky;
    top: 0;
    z-index: 5;
    background-color: rgb(238, 238, 238);
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.import-offer-dialog-content {
    overflow-y: auto;
}

.import-offer-hint {
    color: rgba(0, 0, 0, 0.65);
    font-size: 0.9rem;
}

:deep(.import-offer-row-selected) {
    background-color: rgba(var(--v-theme-primary), 0.12) !important;
}

:deep(.v-data-table__tr) {
    cursor: pointer;
}
</style>
